import { brand } from "../../config/theme";
import { useNavigate, useLocation } from "react-router-dom";
import CompanyScopedOutlet from "../../components/base/CompanyScopedOutlet";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { FUNCTIONALITIES, NUVEX_ACCESS_BASE } from "./config";
import { useAccess } from "../../hooks/useAccess";

// Single shell for every Nuvex Access functionality. Derives the active
// functionality from the URL (/nuvex-access/<slug>/...), renders that
// functionality's sub-menu, and the matched section via <Outlet/>.
// Mirrors SGLayout (Header + AppSidebarMenu + AppsSidebar + Outlet).
export default function NuvexAccessLayout() {
  const { t } = useTranslation("common");
  const { canAccessSectionOfApp } = useAccess();
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Active functionality from URL: /nuvex-access/<slug>/<item>
  const parts = location.pathname.split("/").filter(Boolean); // ["nuvex-access", slug, item]
  const activeSlug = parts[1] ?? FUNCTIONALITIES[0].slug;
  const functionality =
    FUNCTIONALITIES.find((f) => f.slug === activeSlug) ?? FUNCTIONALITIES[0];

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { interpolation: { escapeValue: false }, name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { interpolation: { escapeValue: false }, name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = functionality.menu
    .filter((item) =>
      canAccessSectionOfApp(`nuvex-access-${functionality.slug}`, item.id),
    )
    .map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    path: `${NUVEX_ACCESS_BASE}/${functionality.slug}/${item.id}`,
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  // Alto fijo de la ventana (h-screen = 100vh y luego 100dvh, ver tailwind.config) en columna:
  // banner + encabezado + contenido flex-1/min-h-0. Cada pantalla hace scroll
  // dentro del área de contenido, sin doble scroll aunque salga el banner.
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-app">
      {isExpiringSoon && (
        <div className="shrink-0 bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { interpolation: { escapeValue: false }, time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title="Nuvex Access"
        subtitle={functionality.name}
        onOpenAppsSidebar={openAppsSidebar}
        isAppsSidebarOpen={isAppsSidebarOpen}
        />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) =>
            location.pathname === `${NUVEX_ACCESS_BASE}/${functionality.slug}/${itemId}`
          }
          footerStats={{
            label: functionality.name,
            items: [
              { label: "Funcionalidad", value: functionality.menu.length - 1 },
              { label: "Secciones", value: FUNCTIONALITIES.length, className: "text-primary-500" },
            ],
          }}
        />

        <div className="flex min-h-0 min-w-0 w-full flex-1 justify-center overflow-y-auto" data-testid="nuvex-access-content">
          <CompanyScopedOutlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={`nuvex-access-${functionality.slug}`}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
